import { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/services/api';

// Define User type to match backend User structure
type User = {
  id: string;
  email: string;
  name: string;
  organizationId?: string;
  role: 'admin' | 'user';
};

// Define error response structure for type safety
interface ErrorResponse {
  error: string;
}

type AuthContextType = {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  error: string | null;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      setIsLoading(true);
      try {
        const response = await api.get('/auth/me');
        setUser(response.data.user);

        const token = localStorage.getItem('authToken');
        if (token) {
          api.defaults.headers.common.Authorization = `Bearer ${token}`;
        } else {
          console.warn('[AuthProvider] No token found in localStorage during auth check.');
        }
      } catch (error: any) {
        console.error('[AuthProvider] /auth/me check failed (User likely not authenticated):', error.response?.status, error.response?.data || error.message);
        localStorage.removeItem('authToken');
        delete api.defaults.headers.common.Authorization;
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.post('/auth/login', { email, password });
      const { token, user: loggedInUser } = response.data;

      if (token) {
        localStorage.setItem('authToken', token);
        api.defaults.headers.common.Authorization = `Bearer ${token}`;
      } else {
        console.warn('[AuthProvider] No token received from login endpoint');
      }

      setUser(loggedInUser);
      navigate('/dashboard');
    } catch (error: any) {
      console.error('[AuthProvider] Error during /auth/login API call:', error);
      const errorResponse = error.response?.data as ErrorResponse;
      const errorMessage = errorResponse?.error || 'Login failed. Please try again.';
      setError(errorMessage);
      localStorage.removeItem('authToken');
      delete api.defaults.headers.common.Authorization;
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (name: string, email: string, password: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.post('/auth/register', { name, email, password });
      const { token, user: registeredUser } = response.data;

      if (token) {
        localStorage.setItem('authToken', token);
        api.defaults.headers.common.Authorization = `Bearer ${token}`;
      } else {
        console.warn('[AuthProvider] No token received from registration endpoint');
      }

      setUser(registeredUser);
      navigate('/dashboard');
    } catch (error: any) {
      console.error('[AuthProvider] Error during /auth/register API call:', error);
      const errorResponse = error.response?.data as ErrorResponse;
      const errorMessage = errorResponse?.error || 'Registration failed. Please try again.';
      setError(errorMessage);
      localStorage.removeItem('authToken');
      delete api.defaults.headers.common.Authorization;
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    setIsLoading(true);

    try {
      try {
        await api.post('/auth/logout');
      } catch (logoutError: any) {
        console.warn('[AuthProvider] Logout API call failed:', logoutError.response?.data || logoutError.message);
      }

      localStorage.removeItem('authToken');
      delete api.defaults.headers.common.Authorization;
      setUser(null);
      navigate('/login');
    } catch (error) {
      console.error('[AuthProvider] Logout error:', error);
      localStorage.removeItem('authToken');
      delete api.defaults.headers.common.Authorization;
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        register,
        logout,
        error,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};
